import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * Sets the document title and scrolls correctly for docs pages.
 * When the URL has a hash, scrolls that element into view after paint
 * (instead of always jumping to the top, which would break deep links).
 */
export function useDocsPage(title: string) {
  const { pathname, hash } = useLocation();

  useEffect(() => {
    document.title = title;
  }, [title]);

  useEffect(() => {
    if (!hash) {
      window.scrollTo(0, 0);
      return;
    }

    const id = decodeURIComponent(hash.slice(1));
    if (!id) {
      window.scrollTo(0, 0);
      return;
    }

    let cancelled = false;
    let attempts = 0;

    const scrollToTarget = () => {
      if (cancelled) return;
      const el = document.getElementById(id);
      if (el) {
        el.scrollIntoView({ behavior: 'auto', block: 'start' });
        return;
      }
      attempts += 1;
      if (attempts < 12) {
        requestAnimationFrame(scrollToTarget);
      }
    };

    // Defer past the mount-time layout so sticky header offset applies.
    requestAnimationFrame(scrollToTarget);

    return () => {
      cancelled = true;
    };
  }, [pathname, hash]);
}
